import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS_WITHOUT_CRM } from '@aqx-core/common/api/auth';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useAuthLoginAs } from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { ITabItem } from '@aqx-core/common/types/tab';
import { normalizeSelectedLoginTab } from '@aqx-core/common/utils/auth/normalizeSelectedLoginTab';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

export const useLoginAsTabs = () => {
  const { t } = useTranslation();

  const loginAs = useAuthLoginAs();

  const companyProfile = useCompanyProfile();
  const { multiServerEnabled } = companyProfile ?? {};

  const companyAccess = useMemo(() => validateCompanyAccess(companyProfile), [companyProfile]);

  const {
    hasCompanyLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT4DemoLoginConfig,
    hasMT5LiveLoginConfig,
    hasMT5DemoLoginConfig,
    hasCentroidLiveLoginConfig,
    hasCentroidDemoLoginConfig
  } = companyAccess;

  const tabs = useMemo(() => {
    const list = [];
    const createTab = (value: ELoginAs, label: string, dataTestId: EDataTestId): ITabItem<ELoginAs> => ({
      value,
      label,
      dataTestId
    });

    // Add CRM (LIVE) tab if company login config exists
    if (hasCompanyLoginConfig) {
      list.push(createTab(ELoginAs.LIVE, t('Live'), EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_CRM));
    }

    // Add MT4/MT5 LIVE tab(s)
    const liveLoginType = authenticationViewController.getLiveLoginTypeFromCompanyAccess({
      multiServerEnabled,
      hasMT4LiveLoginConfig,
      hasMT5LiveLoginConfig,
      hasCentroidLiveLoginConfig
    });

    if (liveLoginType) {
      list.push(createTab(liveLoginType, t('Live'), EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_LIVE));
    }

    // Add DEMO tab if available
    const demoLoginType = authenticationViewController.getDemoLoginTypeFromCompanyAccess({
      multiServerEnabled,
      hasMT4DemoLoginConfig,
      hasMT5DemoLoginConfig,
      hasCentroidDemoLoginConfig
    });

    if (demoLoginType) {
      list.push(createTab(demoLoginType, t('Demo'), EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_DEMO));
    }

    return list;
  }, [
    hasCompanyLoginConfig,
    hasCentroidDemoLoginConfig,
    hasCentroidLiveLoginConfig,
    hasMT4DemoLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT5DemoLoginConfig,
    hasMT5LiveLoginConfig,
    multiServerEnabled,
    t
  ]);

  // start undefined (tabs may not be available on first render)
  const [selectedTab, setSelectedTab] = useState<ELoginAs | undefined>(() => loginAs ?? undefined);

  // sync controller when selectedTab is defined
  useEffect(() => {
    if (selectedTab) {
      authenticationViewController.setLoginAs(selectedTab);
    }
  }, [selectedTab]);

  // when tabs become available (or loginAs changes), pick a default if we don't have one yet
  useEffect(() => {
    setSelectedTab((prev) => {
      if (loginAs && multiServerEnabled) {
        if (LIVE_LOGINS_WITHOUT_CRM.includes(loginAs)) {
          authenticationViewController.initPrefilledLogin();
          return ELoginAs.OMS_LIVE;
        } else if (DEMO_LOGINS.includes(loginAs)) {
          return ELoginAs.OMS_DEMO;
        }
      }

      const nextTab = prev ?? tabs[0]?.value;

      if (!DEMO_LOGINS.includes(nextTab)) {
        authenticationViewController.initPrefilledLogin();
      }

      return nextTab;
    });
  }, [tabs, loginAs, multiServerEnabled]);

  return {
    tabs,
    selectedTab: normalizeSelectedLoginTab({ selectedTab, companyAccess, multiServerEnabled }),
    onTabChange: setSelectedTab
  };
};
